import type { BalanceTransactionEntry, V1BillingStatement } from '@nadoutong/contracts';
import { isValidExportRate, moneyYuan } from './money.ts';
import type { SheetCell, SheetSpec } from './types.ts';

/** Chinese headers — always zh, per #178. */
export const STATEMENT_SHEET_HEADERS: readonly string[] = [
  '账期起',
  '账期止',
  '用量（元）',
  '期初应收（元）',
  '本期回款（元）',
  '期末应收（元）',
  '人民币参考期末应收（元）',
  '到期日',
  '状态',
];

export const TRANSACTION_SHEET_HEADERS: readonly string[] = [
  '时间',
  '类型',
  '金额（元）',
  '原因',
  '外部请求 ID',
];

export const STATEMENT_SHEET_NAME = '对账单';
export const TRANSACTION_SHEET_NAME = '余额流水';

const TX_TYPE_LABEL: Readonly<Record<string, string>> = {
  charge: '请求费用',
  gift_card_redeem: '礼品卡入账',
  gift_direct_issue: '赠送入账',
  gift_expiry_reclaim: '赠送到期回收',
  adjustment: '人工调整',
  topup: '充值入账',
  external_topup: '线下充值入账',
};

export interface StatementCellOverrides {
  /** Aligned with `rows`. A number replaces rate-converted 用量（元）. */
  readonly consumptionYuan?: readonly (number | undefined)[];
  /** Aligned with `rows`. True leaves 人民币参考期末应收（元） empty. */
  readonly blankCnyReference?: readonly boolean[];
}

export function assembleStatementSheet(
  rows: readonly V1BillingStatement[],
  rates: readonly number[],
  todayYmd: string,
  overrides?: StatementCellOverrides,
): SheetSpec {
  return {
    name: STATEMENT_SHEET_NAME,
    rows: [
      STATEMENT_SHEET_HEADERS,
      ...rows.map((row, index) =>
        statementRow(row, rateAt(rates, index), todayYmd, {
          consumptionYuan: overrides?.consumptionYuan?.[index],
          blankCnyReference: overrides?.blankCnyReference?.[index] === true,
        }),
      ),
    ],
  };
}

export function assembleTransactionSheet(
  rows: readonly BalanceTransactionEntry[],
  rates: readonly number[],
): SheetSpec {
  return {
    name: TRANSACTION_SHEET_NAME,
    rows: [
      TRANSACTION_SHEET_HEADERS,
      ...rows.map((row, index) => transactionRow(row, rateAt(rates, index))),
    ],
  };
}

function statementRow(
  row: V1BillingStatement,
  cnyPerUsd: number,
  todayYmd: string,
  patch?: { readonly consumptionYuan?: number; readonly blankCnyReference?: boolean },
): readonly SheetCell[] {
  const consumption =
    patch?.consumptionYuan !== undefined
      ? patch.consumptionYuan
      : moneyYuan(row.consumptionUsdMicros, row.consumptionUsdCents, cnyPerUsd);
  const reference =
    patch?.blankCnyReference === true
      ? null
      : row.cnyReferenceClosingArCents === null
        ? null
        : row.cnyReferenceClosingArCents / 100;
  return [
    row.periodStart,
    row.periodEnd,
    consumption,
    moneyYuan(row.openingArUsdMicros, row.openingArUsdCents, cnyPerUsd),
    moneyYuan(row.paymentsAppliedUsdMicros, row.paymentsAppliedUsdCents, cnyPerUsd),
    moneyYuan(row.closingArUsdMicros, row.closingArUsdCents, cnyPerUsd),
    reference,
    row.dueDate,
    statementStatusLabel(row.status, row.dueDate, todayYmd),
  ];
}

function transactionRow(row: BalanceTransactionEntry, cnyPerUsd: number): readonly SheetCell[] {
  return [
    row.createdAt,
    TX_TYPE_LABEL[row.type] ?? row.type.replace(/_/g, ' '),
    moneyYuan(row.amountUsdMicros, row.amountUsdCents, cnyPerUsd),
    row.reason ?? '',
    row.externalRequestId ?? '',
  ];
}

function rateAt(rates: readonly number[], index: number): number {
  const rate = rates[index];
  if (!isValidExportRate(rate)) {
    throw new Error('export display rate unavailable');
  }
  return rate;
}

export function statementStatusLabel(
  status: V1BillingStatement['status'],
  dueDate: string,
  todayYmd: string,
): string {
  if (status === 'closed') return '已结清';
  if (dueDate < todayYmd) return '逾期';
  return '未结清';
}
